// client/src/features/quotations/AddLineToCatalogueModal.tsx
//
// The "Add this item to Master Catalogue" popup for ONE quotation line: `CatalogueItemForm` (the
// SAME form the Master Catalogue's own Add item dialog uses - never a fork) seeded from the
// line's own name and unit. Lifted out of `ItemLookupPanel` so the cost cells can open it
// DIRECTLY (D107: a line with no real vendor cannot get a hand-typed cost - this popup, with its
// required vendor, is how it gets priced).
//
// The caller owns what "saving" means (`onSubmit` is the form's `onSubmitOverride`): the page
// submits the addition for review AND prices the line at once, through
// `addCatalogueItemFromLine`.
import React from 'react';
import { Modal } from '../../components/Modal';
import CatalogueItemForm from '../catalogue/CatalogueItemForm';
import type { CreateCatalogueItemPayload, MasterCatalogueItem } from '../../lib/masterCatalogueApi';

/**
 * What the lookup panel and this popup both search / seed with: the line's own item name. The
 * cleaned `itemName` before `itemDescription` - the catalogue's names come from `cleanItemName`.
 */
export const lineCatalogueName = (item: { itemName?: string | null; itemDescription?: string | null } | undefined): string =>
  String(item?.itemName || item?.itemDescription || '').trim();

interface AddLineToCatalogueModalProps {
  /** The line's name, exactly as it was when the popup opened. */
  itemName: string;
  /** The line's unit. */
  uom?: string | null;
  onClose: () => void;
  onSubmit: (payload: CreateCatalogueItemPayload) => Promise<MasterCatalogueItem | null>;
}

export const AddLineToCatalogueModal: React.FC<AddLineToCatalogueModalProps> = ({ itemName, uom, onClose, onSubmit }) => (
  <Modal
    isOpen
    title="Add this item to Master Catalogue"
    onClose={onClose}
    maxWidthClass="max-w-md"
    // Above the lookup panel's own z-[70] overlay, the same reason the bug reporter's form
    // overrides it (`Modal`'s own doc comment): opened from on top of another popup, it must
    // not render behind it.
    overlayZClass="z-[80]"
  >
    <CatalogueItemForm
      mode={{ kind: 'create' }}
      // Seeded from the LINE, per the product spec - its own item name and unit, never
      // whatever a search box currently holds.
      seed={{ item_name: itemName, unit: uom || undefined }}
      submitLabel="Save item"
      banner={
        <div className="mb-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          This prices this quotation line from what you enter right away, with the vendor you pick. "
          {itemName || 'this item'}" joins the Master Catalogue when this quotation is sent or finalized -
          directly if you are an admin, otherwise after an admin reviews it.
        </div>
      }
      onCancel={onClose}
      onSaved={onClose}
      onSubmitOverride={onSubmit}
    />
  </Modal>
);

export default AddLineToCatalogueModal;
